import React, { useState, useEffect, useMemo, useRef, Suspense, lazy } from 'react';
import { Card, Button, IconButton, Tabs } from 'components/common';
import { Controller, useForm } from "react-hook-form"
import GDS from './form/GDS';
import Areas from './form/Areas';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createPIF, resetExpandAll, setCollapseTabStatus, setExpandAll, updatePIF, updatePIFVersion } from 'redux/crm/project/pifSlice';
import { FaWpforms, FaChevronCircleUp, FaArrowLeft, FaAngleDoubleUp, FaAngleDoubleDown, FaList, FaCalendarAlt } from 'react-icons/fa';
import { MdCancel, MdDataSaverOff } from 'react-icons/md';
import PropTypes from 'prop-types';
import { CRM_PIF_BDS3_PURLIN_GIRT_SPACING } from 'app_config';
import { VALUE_CLS } from './pifStyles';
import { buildAreas, processData, remapAreaFieldNames } from './pifPayload';

const LazyPaintSystemForm = lazy(() => import('./form/PaintSystemForm'));
const LazySecondaryMemberForm = lazy(() => import('./form/SecondaryMemberForm'));

//Area
const LazyAreaFormModal = lazy(() => import("./form/area/AreaFormModal"))
const LazyCloneAreaModal = lazy(() => import("./form/area/CloneAreaModal"))
const LazyAreaDeleteConfirmation = lazy(() => import("./form/AreaDeleteConfirmation"))

//BDS1
const LazyOpenWallForm = lazy(() => import('./form/area/OpenWallForm.js'))

//BDS2
const LazyRoofExtensionForm = lazy(() => import('./form/area/RoofExtensionForm.js'))
const LazyCanopyForm = lazy(() => import('./form/area/CanopyForm.js'))
const LazyFasciaForm = lazy(() => import('./form/area/FasciaForm.js'))
const LazyFrameOpeningForm = lazy(() => import('./form/area/FrameOpeningForm.js'))
const LazyMezzanineForm = lazy(() => import('./form/area/MezzanineForm.js'))
const LazyCraneInformationForm = lazy(() => import('./form/area/CraneInformationForm.js'))

const PIFForm = ({project, building, adaptation, pif, title, isPIFversion, onSave}) => {
  const {control, register, handleSubmit, formState: {errors, isDirty}, setValue, getValues} = useForm();
  const [, setShowWarning] = useState(false);
  const [saving, setSaving] = useState(false);
  const {collapseTabStatus} = useSelector(state => state.crm_project_pif);
  const gds = useMemo(() => pif?.gds || {}, [pif?.gds]);

  // Seeded once into react-hook-form; paint systems and secondary members are
  // kept out of it on purpose (see processData) so this never changes identity.
  const [gdsData] = useState(
    pif ? gds : {
      paint_systems: [],
      secondary_members: []
    }
  )

  const [areaData, setAreaData] = useState(
    pif ? pif.areas : [
      {
        id: 1,
        name: "Area 1",
        // A building has at least one instance, so default the identical-bldgs
        // count to 1 for a fresh PIF instead of leaving it blank.
        bds1: { qty_of_identical_bldgs: 1, building_usage: 1 },
        bds2: {},
        bds3: {}
      }
    ]
  )

  const createInitialState = () => ({
    data: [],
    item: {},
    title: null,
    edit: false,
    showModal: false
  });
  
  const [paintSystem, setPaintSystem] = useState(createInitialState());
  const [secondaryMember, setSecondaryMember] = useState(createInitialState()); 

  const [openWall, setOpenWall] = useState(createInitialState());
  const [roofExtension, setRoofExtension] = useState(createInitialState());
  const [canopy, setCanopy] = useState(createInitialState());
  const [fascia, setFascia] = useState(createInitialState());
  const [frameOpening, setFrameOpening] = useState(createInitialState());
  const [mezzanine, setMezzanine] = useState(createInitialState());
  const [craneInformation, setCraneInformation] = useState(createInitialState());

  const [showAreaModal, setShowAreaModal] = useState(false)
  const [showCloneModal, setShowCloneModal] = useState(false)
  const [showDeleteAreaConfirmation, setShowDeleteAreaConfirmation] = useState(false)
  const [titleArea, setTitleArea] = useState(null)
  const [area, setArea] = useState(null)
  const [draftAreas, setDraftAreas] = useState([])

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const projectId = project?.id
  const buildingId = building?.id
  const adaptationId = adaptation?.id

  const projectData = {
    project,
    building,
    adaptation,
  }

  const useHookForm = {
    Controller,
    control,
    register,
    setValue,
    errors
  }

  useEffect(() => {
    if (gds && Object.keys(gds).length > 0) {
      setPaintSystem({ data: gds?.paint_systems || []});
      setSecondaryMember({ data: gds?.secondary_members || [] });
    }
  }, [gds]);

  // The seven BDS sub-tables (open walls, canopies, cranes…) live in their own
  // state because the user adds rows to them through modals. They are seeded
  // from `areaData` ONCE per area: rebuilding them on every `areaData` change
  // threw away every row the user had just added, because `areaData` is the
  // server snapshot and knows nothing about them.
  //
  // Rows belonging to an area that no longer exists are dropped in the same
  // pass, which is what the old rebuild gave us for free.
  const seededSubTableAreaIdsRef = useRef(new Set())

  useEffect(() => {
    if (!areaData) return

    const currentIds = new Set(areaData.map((area) => String(area.id)))
    const freshAreas = areaData.filter((area) => !seededSubTableAreaIdsRef.current.has(String(area.id)))
    const removedIds = [...seededSubTableAreaIdsRef.current].filter((id) => !currentIds.has(id))

    if (freshAreas.length === 0 && removedIds.length === 0) return

    const rowsOf = (bds, property) =>
      freshAreas.flatMap((area) =>
        (area?.[bds]?.[property] || []).map((row) => ({ ...row, area_id: area.id }))
      )

    const reconcile = (setter, added) =>
      setter((prev) => ({
        ...prev,
        data: [
          ...(prev.data || []).filter((row) => currentIds.has(String(row.area_id))),
          ...added
        ]
      }))

    reconcile(setOpenWall, rowsOf('bds1', 'open_walls'))
    reconcile(setRoofExtension, rowsOf('bds2', 'roof_extensions'))
    reconcile(setCanopy, rowsOf('bds2', 'canopies'))
    reconcile(setFascia, rowsOf('bds2', 'fascias'))
    reconcile(setFrameOpening, rowsOf('bds2', 'frame_openings'))
    reconcile(setMezzanine, rowsOf('bds2', 'mezzanines'))
    reconcile(setCraneInformation, rowsOf('bds2', 'crane_informations'))

    removedIds.forEach((id) => seededSubTableAreaIdsRef.current.delete(id))
    freshAreas.forEach((area) => seededSubTableAreaIdsRef.current.add(String(area.id)))
  }, [areaData])

  // Seed react-hook-form with every area's BDS values up-front, independent of
  // which tab is currently mounted. The BDS tabs use `mountOnEnter` for render
  // performance, so their own on-mount `setValue` effects never run for tabs the
  // user hasn't opened — leaving those fields unregistered and silently dropped
  // from getValues()/handleSubmit (data loss on Save, Clone Area, and Copy PIF).
  // Seeding here guarantees the full form state exists regardless of mounting.
  // Each area is seeded once (tracked by id) so user edits are never clobbered,
  // and newly added/cloned areas are seeded when they appear in areaData.
  const seededAreaIdsRef = useRef(new Set())

  useEffect(() => {
    if (!areaData) return

    areaData.forEach((area) => {
      const areaKey = String(area.id)
      if (seededAreaIdsRef.current.has(areaKey)) return

      // Default for a field the user may also type into — only when it is still
      // empty, otherwise the saved value is overwritten every time we seed.
      if (!area.bds3?.purlin_girt_spacing) {
        setValue(`bds3_purlin_girt_spacing_${area.id}`, CRM_PIF_BDS3_PURLIN_GIRT_SPACING)
      }

      const bdsSections = [
        ['bds1', area.bds1],
        ['bds2', area.bds2],
        ['bds3', area.bds3]
      ]

      bdsSections.forEach(([prefix, bdsData]) => {
        if (bdsData) {
          Object.entries(bdsData).forEach(([key, value]) => {
            setValue(`${prefix}_${key}_${area.id}`, value)
          })
        }
      })

      seededAreaIdsRef.current.add(areaKey)
    })
  }, [areaData, setValue])

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        const message = "You have unsaved changes. Are you sure you want to leave?";
        e.returnValue = message; // Standard for most browsers
        return message; // For some browsers like older versions of Firefox
      }
    };

    if (isDirty) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload); // Cleanup on unmount
    };
  }, [isDirty]);

  // Everything the pure payload builders need that lives in component state.
  // Built fresh on each call so a stale closure can never ship stale rows.
  const payloadContext = () => ({
    areaData,
    openWalls: openWall.data,
    roofExtensions: roofExtension.data,
    canopies: canopy.data,
    fascias: fascia.data,
    frameOpenings: frameOpening.data,
    mezzanines: mezzanine.data,
    craneInformations: craneInformation.data,
    paintSystems: paintSystem.data,
    secondaryMembers: secondaryMember.data
  })

  // Renaming an area changes its id, which is embedded in every react-hook-form
  // field name and in every sub-table row. All three have to move together, so
  // the rename happens here where that state lives rather than in the modal.
  const handleRenameArea = (previousId, nextId) => {
    const moved = remapAreaFieldNames(getValues(), previousId, nextId)
    Object.entries(moved).forEach(([field, value]) => setValue(field, value))

    const retagRows = (setter) =>
      setter((prev) => ({
        ...prev,
        data: (prev.data || []).map((row) =>
          String(row.area_id) === String(previousId) ? { ...row, area_id: nextId } : row
        )
      }))

    ;[setOpenWall, setRoofExtension, setCanopy, setFascia,
      setFrameOpening, setMezzanine, setCraneInformation].forEach(retagRows)

    seededSubTableAreaIdsRef.current.delete(String(previousId))
    seededSubTableAreaIdsRef.current.add(String(nextId))
    seededAreaIdsRef.current.add(String(nextId))

    setAreaData((prev) =>
      prev.map((area) =>
        String(area.id) === String(previousId)
          ? { ...area, id: nextId, name: `Area ${nextId}` }
          : area
      )
    )
  }

  const areaObject = {
    areaData,
    setAreaData,
    onRenameArea: handleRenameArea
  }

  const onSubmit = async (data, event) => {
    if (saving) return;

    const result = processData(data, payloadContext());

    const actionType = event.nativeEvent.submitter.value;
    const saveAndClose = actionType === "createOrUpdate";

    try {
      setSaving(true);

      // Caller-managed save (e.g. a change order revision's PIF) — it owns the
      // dispatch and where to go afterwards.
      if (onSave) {
        await onSave(result, { saveAndClose });
        setShowWarning(false);
        return;
      }

      let pifId = null

      if (pif) {
        pifId = pif.id

        if (isPIFversion) {
          await dispatch(updatePIFVersion({projectId, buildingId, adaptationId, pifId, data: result }));
          toast.success("PIF was successful updated.");
        }
        else {
          await dispatch(updatePIF({projectId, buildingId, adaptationId, pifId, data: result }))
        }
      }
      else {
        const response = await dispatch(createPIF({projectId, buildingId, adaptationId, data: result}))

        pifId = response.payload.data?.id;

        // if (pifId)
        // {
        //   const buildingUrl = `/crm/projects/${projectId}/buildings/${buildingId}`;
        //   const pifUrl = isPIFversion ? `${buildingUrl}/change-orders/${adaptationId}/pifs-versions/${pifId}` : `${buildingUrl}/adaptations/${adaptationId}/pifs/${pifId}`;
          
        //   navigate(pifUrl)
        // }
      }

      if (pifId) {
        const buildingUrl = `/crm/projects/${projectId}/buildings/${buildingId}`;
        const pifUrl = isPIFversion ? `${buildingUrl}/change-orders/${adaptationId}/pifs-versions/${pifId}` : `${buildingUrl}/adaptations/${adaptationId}/pifs/${pifId}`;

        if (saveAndClose) {
          navigate(pifUrl)
        }
      }

      setShowWarning(false);
    }
    catch(error) {
      toast.error(error.message);
    }
    finally {
      setSaving(false);
    }
  }

  const createMainFormState = (prevState, title, edit, smItem = null, areaId = null) => ({
    ...prevState,
    title,
    edit,
    item: smItem,
    area_id: areaId,
    showModal: true,
  });

  const handleDeleteAction = (stateUpdater, setStateUpdater, item, entityName, areaId, successMessage=`${entityName} ID#${item.id} was removed.`) => {
    let newData = null

    if (areaId) {
      newData = stateUpdater.data.filter(
        (dataItem) => !(dataItem.id === item.id && dataItem.area_id === areaId)
      )
    }
    else {
      newData = stateUpdater.data.filter((sitem) => item.id !== sitem.id)
    }

    setStateUpdater((preState) => 
      ({
        ...preState,
        data: newData
      })
    )
    toast.success(successMessage)
  }

  // GDS
  const handleNewPaintSystem = () => {
    setPaintSystem(createMainFormState(paintSystem, "Create a new Paint system", false, null, null));
  }

  const handleEditPaintSystem = (item) => {
    setPaintSystem(createMainFormState(paintSystem, "Edit Paint system", true, item, null));
  }

  const handleDeletePaintSystem = (item) => {
    handleDeleteAction(paintSystem, setPaintSystem, item, "Paint system", null, `Paint system Cost no #${item.cost_no} was removed.`)
  }

  const handleNewSecondaryMember = () => {
    setSecondaryMember(createMainFormState(secondaryMember, "Create a new Secondary Member", false, null, null));
  }

  const handleEditSecondaryMember = (item) => {
    setSecondaryMember(createMainFormState(secondaryMember, "Edit Secondary Member", true, item, null));
  }

  const handleDeleteSecondaryMember = (item) => {
    handleDeleteAction(secondaryMember, setSecondaryMember, item, "Secondary Member", null, `Secondary Memeber Cost no #${item.cost_no} was removed.`)
  }


  //Area => BDS
  const handleNewOpenWall = (areaId) => {
    setOpenWall(createMainFormState(openWall, "Create a new Open Wall", false, null, areaId));
  }

  const handleEditOpenWall = (areaId, item) => {
    setOpenWall(createMainFormState(openWall, "Edit Open Wall", true, item, areaId));
  }

  const handleDeleteOpenWall = (areaId, item) => {
    handleDeleteAction(openWall, setOpenWall, item, "Open Wall", areaId, `Open Wall location #${item.location} was removed.`)
  }

  const handleNewRoofExtension = (areaId) => {
    setRoofExtension(createMainFormState(roofExtension, "Create a new Roof Entension", false, null, areaId));
  }

  const handleEditRoofExtension = (areaId, item) => {
    setRoofExtension(createMainFormState(roofExtension, "Edit Roof Entension", true, item, areaId));
  }

  const handleDeleteRoofExtension = (areaId, item) => {
    handleDeleteAction(roofExtension, setRoofExtension, item, "Roof Extension", areaId, `Roof Extension location #${item.location} was removed.`)
  }

  const handleNewCanopy = (areaId) => {
    setCanopy(createMainFormState(canopy, "Create a new Canopy", false, null, areaId));
  }

  const handleEditCanopy = (areaId, item) => {
    setCanopy(createMainFormState(canopy, "Edit Canopy", true, item, areaId));
  }

  const handleDeleteCanopy = (areaId, item) => {
    handleDeleteAction(canopy, setCanopy, item, canopy, areaId, `Canopy location #${item.location} was removed.`)
  }

  const handleNewFascia = (areaId) => {
    setFascia(createMainFormState(fascia, "Create a new Fascia", false, null, areaId));
  }

  const handleEditFascia = (areaId, item) => {
    setFascia(createMainFormState(fascia, "Edit Fascia", true, item, areaId));
  }

  const handleDeleteFascia = (areaId, item) => {
    handleDeleteAction(fascia, setFascia, item, "Fascia", areaId, `Fascia location #${item.location} was removed.`)
  }

  const handleNewFrameOpening = (areaId) => {
    setFrameOpening(createMainFormState(frameOpening, "Create a new Frame Opening", false, null, areaId));
  }

  const handleEditFrameOpening = (areaId, item) => {
    setFrameOpening(createMainFormState(frameOpening, "Edit Frame Opening", true, item, areaId));
  }

  const handleDeleteFrameOpening = (areaId, item) => {
    handleDeleteAction(frameOpening, setFrameOpening, item, "Frame Opening", areaId, `Frame Opening location #${item.location} was removed.`)
  }

  const handleNewMezzanine = (areaId) => {
    setMezzanine(createMainFormState(mezzanine, "Create a new Mezzanine", false, null, areaId));
  }

  const handleEditMezzanine = (areaId, item) => {
    setMezzanine(createMainFormState(mezzanine, "Edit Mezzanineg", true, item, areaId));
  }

  const handleDeleteMezzanine = (areaId, item) => {
    handleDeleteAction(mezzanine, setMezzanine, item, "Mezzanineg", areaId, `Mezzanine Mz no.#${item.mz_no} was removed.`, "mz_no")
  }

  const handleNewCraneInformation = (areaId) => {
    setCraneInformation(createMainFormState(craneInformation, "Create a new Crane Information", false, null, areaId));
  }

  const handleEditCraneInformation = (areaId, item) => {
    setCraneInformation(createMainFormState(craneInformation, "Edit Crane Information", true, item, areaId));
  }

  const handleDeleteCraneInformation = (areaId, item) => {
    handleDeleteAction(craneInformation, setCraneInformation, item, "Crane", areaId, `Crane Information no.#${item.no} was removed.`, "crane_sr")
  }

   const handleNewArea = () => {
    setTitleArea("Create new Area")
    setShowAreaModal(true)
    setArea(null)
  }

  const handleEditArea = (area) => {
    setTitleArea("Edit Area")
    setShowAreaModal(true)
    setArea(area)
  }

  const handleCloneArea = () => {
    setShowCloneModal(true)

    // Snapshot the current form state per area so the clone modal can copy from
    // an area even if its tab was never opened this session.
    setDraftAreas(buildAreas(getValues(), payloadContext()))
  }

  const handleDeleteArea = (area) => {
    setShowDeleteAreaConfirmation(true)
    setArea(area)
  }

  const handleSubmitAreaDelete = () => {
    const newAreaData = areaData.filter((item) => item.id !== area.id)
    if (newAreaData) {
      setAreaData(newAreaData)
      setShowDeleteAreaConfirmation(false)
      toast.success(`Area ${area.id} was successful deleted.`)
    }
    else {
      toast.error(`Error, something went wrong deleting Area ${area.id}.`)
   }
  }

  const handleExpandAll = () => {
    if (collapseTabStatus) {
      dispatch(setExpandAll())
    }
    else {
      dispatch(resetExpandAll())
    }

    dispatch(setCollapseTabStatus(!collapseTabStatus))
  }

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  return (
    <>
      <Card
        padding="none"
        className="tw-mb-3"
        header={
          <div className="tw-flex tw-items-center tw-justify-between">
            <IconButton
              onClick={() => navigate(-1)}
              size="sm"
              icon={<FaArrowLeft size={12} />}
            />
            <IconButton
              size="sm"
              icon={collapseTabStatus ? <FaAngleDoubleDown size={12} /> : <FaAngleDoubleUp size={12} />}
              onClick={handleExpandAll}
            >
              <span className="tw-hidden xl:tw-inline-block">Collapse All</span>
            </IconButton>
          </div>
        }
      >
        <div className="tw-p-4 tw-text-sm tw-text-center">
          <h5 className="tw-font-semibold tw-mb-2">{title} Project Information Form (PIF)</h5>
          <div>Project: <span className={`tw-font-semibold ${VALUE_CLS}`}>{project?.name}</span></div>
          <div>Building: <span className={`tw-font-semibold ${VALUE_CLS}`}>{building?.name}</span></div>

          {adaptation?.type == "change_order" ? (
            <div>CO#: <span className={`tw-font-semibold ${VALUE_CLS}`}>{adaptation?.co}</span></div>
          ) : (
            <div>REV#: <span className={`tw-font-semibold ${VALUE_CLS}`}>{adaptation?.rev}</span></div>
          )}
        </div>
      </Card>

      <Card padding="none" className="tw-mb-20">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Tabs
            variant="underline"
            lazyMount
            navClassName="tw-px-3 tw-pt-2 tw-bg-white"
            contentClassName="tw-bg-slate-50 tw-p-4"
            tabs={[
              {
                label: 'General Data Sheet (GDS)',
                icon: <FaList size={13} />,
                content: (
                  <GDS
                    projectData={projectData}
                    gdsData={gdsData}
                    pifId={pif?.id}
                    useHookForm={useHookForm}

                    paintSystemData={paintSystem.data}
                    newPaintSystem={handleNewPaintSystem}
                    editPaintSystem={handleEditPaintSystem}
                    deletePaintSystem={handleDeletePaintSystem}

                    secondaryMemberData={secondaryMember.data}
                    newSecondaryMember={handleNewSecondaryMember}
                    editSecondaryMember={handleEditSecondaryMember}
                    deleteSecondaryMember={handleDeleteSecondaryMember}
                  />
                )
              },
              {
                label: 'Areas',
                icon: <FaCalendarAlt size={13} />,
                content: (
                  <Areas
                    projectData={projectData}
                    useHookForm={useHookForm}
                    areaObject={areaObject}
                    newArea={handleNewArea}
                    editArea={handleEditArea}
                    cloneArea={handleCloneArea}
                    deleteArea={handleDeleteArea}
                    openWallData={openWall.data}
                    newOpenWall={handleNewOpenWall}
                    editOpenWall={handleEditOpenWall}
                    deleteOpenWall={handleDeleteOpenWall}
                    roofExtensionData={roofExtension.data}
                    newRoofExtension={handleNewRoofExtension}
                    editRoofExtension={handleEditRoofExtension}
                    deleteRoofExtension={handleDeleteRoofExtension}
                    canopyData={canopy.data}
                    newCanopy={handleNewCanopy}
                    editCanopy={handleEditCanopy}
                    deleteCanopy={handleDeleteCanopy}
                    fasciaData={fascia.data}
                    newFascia={handleNewFascia}
                    editFascia={handleEditFascia}
                    deleteFascia={handleDeleteFascia}
                    frameOpeningData={frameOpening.data}
                    newFrameOpening={handleNewFrameOpening}
                    editFrameOpening={handleEditFrameOpening}
                    deleteFrameOpening={handleDeleteFrameOpening}
                    mezzanineData={mezzanine.data}
                    newMezzanine={handleNewMezzanine}
                    editMezzanine={handleEditMezzanine}
                    deleteMezzanine={handleDeleteMezzanine}
                    craneInformationData={craneInformation.data}
                    newCraneInformation={handleNewCraneInformation}
                    editCraneInformation={handleEditCraneInformation}
                    deleteCraneInformation={handleDeleteCraneInformation}
                  />
                )
              }
            ]}
          />

          {/* Buttons must stay inside the <form>: onSubmit reads
              event.nativeEvent.submitter.value from name/value below */}
          <div className="tw-fixed tw-bottom-0 tw-inset-x-0 tw-z-[1030] tw-bg-white tw-border-t tw-border-solid tw-border-slate-200 tw-py-3 tw-text-center tw-shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
            {pif && (
              <Button size="sm" type="submit" name="action" value="saveAsDraft" className="tw-mr-1" disabled={saving}>
                <MdDataSaverOff /> {saving ? 'Saving…' : 'Save'}
              </Button>
            )}
            <Button size="sm" type="submit" className="tw-mr-1" name="action" value="createOrUpdate" disabled={saving}>
              <FaWpforms /> {saving ? 'Saving…' : (pif ? 'Save and close' : 'Create PIF')}
            </Button>
            <Button size="sm" variant="danger" onClick={() => navigate(-1)} disabled={saving}> <MdCancel />{pif ? 'Close' : 'Cancel'}</Button>
          </div>
        </form>

        <div className="tw-text-sm tw-m-2 tw-text-right">{}</div>
      </Card>

      <div 
        onClick={scrollToTop} 
        style={styles.backToTopButton}
        title="Back to top"
        size='sm'
      >
        <FaChevronCircleUp />
      </div>

      <Suspense fallback={<div className='tw-text-center tw-p-3'>Loading....</div>}>
        {paintSystem && paintSystem.showModal && (
          <LazyPaintSystemForm
            dataObject={paintSystem}
            setDataObject={setPaintSystem}
          />
        )}

        {secondaryMember && secondaryMember.showModal && (
          <LazySecondaryMemberForm
            dataObject={secondaryMember}
            setDataObject={setSecondaryMember}
          />
        )}

        {openWall && openWall.showModal && (
          <LazyOpenWallForm
            dataObject={openWall}
            setDataObject={setOpenWall}
          />
        )}

        {roofExtension && roofExtension.showModal && (
          <LazyRoofExtensionForm
            dataObject={roofExtension}
            setDataObject={setRoofExtension}
          />
        )}

        {canopy && canopy.showModal && (
          <LazyCanopyForm 
            dataObject={canopy}
            setDataObject={setCanopy}
          />
        )}

        {fascia && fascia.showModal && (
          <LazyFasciaForm
            dataObject={fascia}
            setDataObject={setFascia}
          />
        )}

        {frameOpening && frameOpening.showModal && (
          <LazyFrameOpeningForm
            dataObject={frameOpening}
            setDataObject={setFrameOpening}
          />
        )}

        {mezzanine && mezzanine.showModal && (
          <LazyMezzanineForm
            dataObject={mezzanine}
            setDataObject={setMezzanine}
          />
        )}

        {craneInformation && craneInformation.showModal && (
          <LazyCraneInformationForm
            dataObject={craneInformation}
            setDataObject={setCraneInformation}
          />
        )}

        {showAreaModal && (
          <LazyAreaFormModal
            showModal={showAreaModal}
            setshowModal={setShowAreaModal}
            title={titleArea}
            areaObject={areaObject}
            area={area}
          />
        )}
        
        {showCloneModal && (
          <LazyCloneAreaModal
            showModal={showCloneModal}
            setshowModal={setShowCloneModal}
            areaObject={areaObject}
            draftAreas={draftAreas}
          />
        )}

        {showDeleteAreaConfirmation && (
          <LazyAreaDeleteConfirmation
            showModal={showDeleteAreaConfirmation}
            setshowModal={setShowDeleteAreaConfirmation}
            area={area}
            onSubmitAreaDelete={handleSubmitAreaDelete}
          />
        )}

      </Suspense>
    </>
  )
};

const styles = {
  backToTopButton: {
    position: 'fixed',
    bottom: '15px',
    right: '35px',
    cursor: 'pointer',
    fontSize: '26px',
    zIndex: '2000'
  }
};

PIFForm.propTypes = {
  project: PropTypes.object.isRequired,
  building: PropTypes.object.isRequired,
  adaptation: PropTypes.object.isRequired,
  pif: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  isPIFversion: PropTypes.bool.isRequired,
  // When given, the caller owns persisting the PIF and the post-save navigation.
  onSave: PropTypes.func,
}

export default PIFForm;
